import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from 'services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import type { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { Exclusion, OpenPeriod, Reference, ScheduleUI } from 'pages/apps/platform/scheduling_window/types';

export interface SchedulingWindow {
  scheduling_window_id?: number;
  name: string;
  timezone: string;
  schedules: ScheduleUI[];
  references: Reference[];
  exclusions: Exclusion[];
  description: string;
  zone_name: string;
  is_calendar?: boolean;
  day_rollover?: string | null;
  min_open_duration_ms?: number | null;
  // Folder support (nap-backend #1076). Zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface HeaderConfig {
  header_key: string;
  header_type: string;
  label: string;
}

interface LoadingState {
  fetchSchedulingWindows: boolean;
  postSchedulingWindow: boolean;
  putSchedulingWindow: boolean;
  deleteSchedulingWindow: boolean;
  evaluateSchedulingWindow: boolean;
}

interface EvaluationState {
  periods: OpenPeriod[];
  error: string | null;
}

interface State {
  nodes: SchedulingWindow[];
  items: SchedulingWindow[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: HeaderConfig[];
  filtersConfig: FilterConfig[];
  loading: LoadingState;
  evaluation: EvaluationState;
}

const initialState: State = {
  nodes: [],
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchSchedulingWindows: false,
    postSchedulingWindow: false,
    putSchedulingWindow: false,
    deleteSchedulingWindow: false,
    evaluateSchedulingWindow: false,
  },
  evaluation: {
    periods: [],
    error: null,
  },
};

export const fetchSchedulingWindows = createAsyncThunk('schedulingWindows/fetch', async (params: Record<string, any> = {}) => {
  const cleanParams = {
    ...params,
    sortColumn: params.sortColumn || 'created_at',
    sortOrder: (params.sortOrder || 'ASC').toUpperCase(),
    page: params.page || 1,
    limit: params.limit || 2000,
  };

  const res = await api.get('/scheduling_windows', {
    params: flattenQuery(cleanParams),
  });
  return res.data;
});

export const postSchedulingWindow = createAsyncThunk('schedulingWindows/create', async (data: Partial<SchedulingWindow>, { rejectWithValue }) => {
  try {
    const res = await api.post('/scheduling_windows', data);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
  }
});

export const putSchedulingWindow = createAsyncThunk(
  'schedulingWindows/update',
  async (
    {
      zone_name,
      window_name,
      data,
    }: {
      zone_name: string;
      window_name: string;
      data: Partial<SchedulingWindow>;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put(`/scheduling_windows/${zone_name}/${window_name}`, data);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const deleteSchedulingWindow = createAsyncThunk(
  'schedulingWindows/delete',
  async ({ zone_name, window_name }: { zone_name: string; window_name: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/scheduling_windows/${zone_name}/${window_name}`);
      return { zone_name, window_name };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const evaluateSchedulingWindow = createAsyncThunk(
  'schedulingWindows/evaluate',
  async (
    {
      zone_name,
      window_name,
      timezone,
    }: {
      zone_name: string;
      window_name: string;
      timezone: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(`/scheduling_windows/${encodeURIComponent(zone_name)}/${encodeURIComponent(window_name)}/evaluate`, {
        timestamp: new Date().toISOString(),
        timezone,
      });
      return res.data.schedule_open_in_given_period || [];
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message || 'Failed to evaluate schedule'));
    }
  }
);

const schedulingWindowSlice = createSlice({
  name: 'schedulingWindow',
  initialState,
  reducers: {
    clearEvaluation: (state) => {
      state.evaluation.periods = [];
      state.evaluation.error = null;
    },
  },
  extraReducers: (b) => {
    // fetchSchedulingWindows
    b.addCase(fetchSchedulingWindows.pending, (state) => {
      state.status = 'loading';
      state.error = null;
      state.loading.fetchSchedulingWindows = true;
    });
    b.addCase(fetchSchedulingWindows.fulfilled, (state, action) => {
      const { nodes = [], pagination, config = [], filtersConfig = [] } = action.payload || {};

      state.nodes = nodes;
      state.items = nodes;
      state.config = config;
      state.filtersConfig = filtersConfig;

      state.pagination.totalItems = pagination?.total_records ?? 0;
      state.pagination.totalPages = pagination?.total_pages ?? 0;
      state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
      const cp = pagination?.current_page ?? 1;
      const tp = pagination?.total_pages ?? 0;
      state.pagination.currentPage = cp > tp && tp > 0 ? 1 : cp;

      state.status = 'succeeded';
      state.loading.fetchSchedulingWindows = false;
    });
    b.addCase(fetchSchedulingWindows.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch scheduling windows';
      state.loading.fetchSchedulingWindows = false;
    });

    // postSchedulingWindow
    b.addCase(postSchedulingWindow.pending, (state) => {
      state.loading.postSchedulingWindow = true;
    });
    b.addCase(postSchedulingWindow.fulfilled, (state, action) => {
      state.loading.postSchedulingWindow = false;
      state.nodes.push(action.payload);
      state.items = state.nodes;
      const toastId = 'data-succedeed-to-create-scheduling-window';
      if (!toast.isActive(toastId)) {
        toast.success('Successfully created new scheduling window!', {
          toastId,
        });
      }
    });
    b.addCase(postSchedulingWindow.rejected, (state, action) => {
      state.loading.postSchedulingWindow = false;
      const msg = (action.payload as { error?: string } | undefined)?.error || action.error.message || 'An error occurred';
      state.error = msg;
      const toastId = 'data-failed-to-create-scheduling-window';
      if (!toast.isActive(toastId)) toast.error(msg, { toastId });
    });

    // putSchedulingWindow
    b.addCase(putSchedulingWindow.pending, (state) => {
      state.loading.putSchedulingWindow = true;
    });
    b.addCase(putSchedulingWindow.fulfilled, (state, action) => {
      state.loading.putSchedulingWindow = false;
      const idx = state.nodes.findIndex((n) => n.scheduling_window_id === action.payload.scheduling_window_id);
      if (idx !== -1) {
        state.nodes[idx] = { ...state.nodes[idx], ...action.payload };
        state.items = state.nodes;
      }
      const toastId = 'data-succedeed-to-update-scheduling-window';
      if (!toast.isActive(toastId)) {
        toast.success('Successfully updated scheduling window!', { toastId });
      }
    });
    b.addCase(putSchedulingWindow.rejected, (state, action) => {
      state.loading.putSchedulingWindow = false;
      const msg = (action.payload as { error?: string } | undefined)?.error || action.error.message || 'An error occurred';
      state.error = msg;
      const toastId = 'data-failed-to-update-scheduling-window';
      if (!toast.isActive(toastId)) toast.error(msg, { toastId });
    });

    // deleteSchedulingWindow
    b.addCase(deleteSchedulingWindow.pending, (state) => {
      state.loading.deleteSchedulingWindow = true;
    });
    b.addCase(deleteSchedulingWindow.fulfilled, (state, action) => {
      state.loading.deleteSchedulingWindow = false;
      state.nodes = state.nodes.filter((n) => !(n.zone_name === action.payload.zone_name && n.name === action.payload.window_name));
      state.items = state.nodes;
    });
    b.addCase(deleteSchedulingWindow.rejected, (state, action) => {
      state.loading.deleteSchedulingWindow = false;
      const msg = (action.payload as { error?: string } | undefined)?.error || action.error.message || 'An error occurred';
      state.error = msg;
      const toastId = 'data-failed-to-delete-scheduling-window';
      if (!toast.isActive(toastId)) toast.error(msg, { toastId });
    });

    b.addCase(evaluateSchedulingWindow.pending, (state) => {
      state.loading.evaluateSchedulingWindow = true;
      state.evaluation.error = null;
      state.evaluation.periods = [];
    });
    b.addCase(evaluateSchedulingWindow.fulfilled, (state, action) => {
      state.loading.evaluateSchedulingWindow = false;
      state.evaluation.periods = action.payload;
    });
    b.addCase(evaluateSchedulingWindow.rejected, (state, action) => {
      state.loading.evaluateSchedulingWindow = false;
      const msg = (action.payload as { error?: string } | undefined)?.error || action.error.message || 'Failed to evaluate schedule';
      state.evaluation.error = msg;
    });
  },
});

export const { clearEvaluation } = schedulingWindowSlice.actions;
export default schedulingWindowSlice.reducer;
